import { router } from 'expo-router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';

import { useIsNavigationReady } from '@fhs/utils';
import { Icon } from '@fhs-legacy/universal-components';
import CartLoading from 'components/cart-loading';
import OrderUnavailableModal from 'components/modal-order-unavailable';
import ModalSurprise from 'components/modal-surprise';
import { ModalUpsize } from 'components/modal-upsize';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import useAuth from 'hooks/auth/use-auth';
import useAuthRedirects from 'hooks/auth/use-auth-redirects';
import { useIsMobileBp } from 'hooks/breakpoints';
import useFeatureMenuAddOn from 'hooks/use-feature-menu-add-on';
import { CustomScrollViewProps } from 'pages/cart/payment/types';
import { LoyaltyRewardsBalance } from 'pages/loyalty/loyalty-rewards-balance';
import { useCartContext } from 'state/cart';
import { useLoyaltyContext } from 'state/loyalty';
import { useOffersContext } from 'state/offers';
import { OrderStatus, useOrderContext } from 'state/order';
import { OrderSuccessFailureStatuses } from 'state/order/constants';
import { usePaymentContext } from 'state/payment';
import { useRestaurantPosConnectivityStatus } from 'state/restaurant-connectivity-status';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { theme } from 'styles/configure-theme';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { useFlag } from 'utils/launchdarkly';
import { getDateNowByTimezone, isRestaurantOpen } from 'utils/restaurant';
import { routes } from 'utils/routing';
import { isCatering as isCateringOrder, isDelivery } from 'utils/service-mode';
import { CART_PAGE } from 'utils/test-ids';

import CartContent from './cart-content';
import { CartPageError } from './cart-page-error';
import { CartContentWrapper } from './styled';

export const StyledBackIcon = Icon.withConfig({
  variant: 'cancel',
  color: theme.token('icon-default'),
  ...hiddenAccessibilityPlatformProps,
});

export const Cart = () => {
  const {
    cartEntries,
    serverOrder,
    unavailableCartEntries,
    orderStatus,
    cardSuccessfullyAdded,
    tryPricing,
    openUnavailableErrorModal,
    isCommittingOrder,
  } = useCartContext();
  const { serviceMode } = useServiceModeContext();
  const { formatMessage } = useIntl();
  const { isAuthenticated } = useAuth();
  // TODO - FPF Cart this use of order context can be completely remove from here into the new cart context
  const {
    calculatedCartTotal,
    isDonationZeroAmountSelected,
    setIsDonationZeroAmountSelected,
    emptyCart,
  } = useOrderContext();
  // Retrieve payment processor to determine which payment page to display
  const { checkoutPaymentMethod } = usePaymentContext();
  const offersCtx = useOffersContext();
  const { selectedOfferCartEntry } = offersCtx;
  const enableSignupAfterCart = useFlag('enable-move-signup-after-cart');
  const { store } = useStoreContext();
  const { loyaltySurpriseOffersEnabled } = useLoyaltyContext();

  const cartFullPageScreenRef = useRef<CustomScrollViewProps>();

  const storeId = store.number || '';
  const isDeliveryRestaurantClosed =
    isDelivery(serviceMode) &&
    !!storeId &&
    !isRestaurantOpen(
      store.deliveryHours,
      getDateNowByTimezone({ timeZone: store.timezone || undefined })
    );

  const { loading: isRestaurantPosConnectivityStatusLoading } = useRestaurantPosConnectivityStatus({
    storeId,
    skip: isDeliveryRestaurantClosed,
  });

  const { sections: addOnSections } = useFeatureMenuAddOn();

  const isMobile = useIsMobileBp();

  const [showUnavailableItemsModal, setShowUnavailableItemsModal] = useState(false);

  // DO NOT USE service mode on order context. Use service mode on server order
  const orderServiceMode = serverOrder?.cart?.serviceMode;

  // Don't redirect to SignIn/SignUp if:
  // - The flag enable-signup-after-cart is enabled
  useAuthRedirects({ skip: enableSignupAfterCart });

  const isCartTotalNegative = calculatedCartTotal < 0;

  const handleUnavailableModalDismiss = useCallback(() => {
    setShowUnavailableItemsModal(false);
    tryPricing();
  }, [tryPricing]);

  useEffect(() => {
    setShowUnavailableItemsModal(!!unavailableCartEntries.length);
  }, [unavailableCartEntries.length]);

  // tryPricing is triggered by cart context when cart/offers change (single source)

  // Redirect to Menu if:
  // - Cart is empty
  // - Selected offer is null
  // - The order has not been inserted or has been requested
  // Or
  // - Service mode is null
  const shouldRedirect =
    (!cartEntries.length &&
      !selectedOfferCartEntry &&
      !OrderSuccessFailureStatuses.success.pos.includes(orderStatus as OrderStatus) &&
      !OrderSuccessFailureStatuses.inProgress.pos.includes(orderStatus as OrderStatus) &&
      !OrderSuccessFailureStatuses.success.catering.includes(orderStatus as OrderStatus)) ||
    (!serviceMode && !orderServiceMode);
  const isServiceModeCatering = isCateringOrder(serviceMode);

  const isRootNavigationReady = useIsNavigationReady();

  // Takes care of the navigation on hitting back to cart after placing an order
  useEffect(() => {
    if (!isRootNavigationReady) {
      return;
    }
    if (orderStatus === OrderStatus.INSERT_SUCCESSFUL && serverOrder?.rbiOrderId) {
      //  Redirecting to order confirmation from cart - order has been placed
      emptyCart();
      router.dismissTo({
        pathname: `${routes.orderConfirmation}/${serverOrder.rbiOrderId}`,
        params: { showToast: 'true' },
      });
    }
  }, [isRootNavigationReady, orderStatus, serverOrder?.rbiOrderId, emptyCart]);

  useEffect(() => {
    if (!isRootNavigationReady) {
      return;
    }
    if (shouldRedirect) {
      if (isDonationZeroAmountSelected) {
        setIsDonationZeroAmountSelected(false);
      }
      if (isServiceModeCatering) {
        // TODO: expo-router - test this when we have catering working
        router.replace(routes.catering);
      } else {
        router.dismissTo(routes.menu);
      }
    }
  }, [
    isDonationZeroAmountSelected,
    isRootNavigationReady,
    isServiceModeCatering,
    setIsDonationZeroAmountSelected,
    shouldRedirect,
  ]);

  if (shouldRedirect) {
    return null;
  }

  // handles browser back button after order commit
  if (
    (orderStatus === OrderStatus.INSERT_SUCCESSFUL && serverOrder?.rbiOrderId) ||
    isCommittingOrder
  ) {
    return <CartLoading />;
  }

  // Show the loading cart if:
  // - User is authenticated or flag enable-signup-after-cart is disabled
  // - Is restaurant online or is pricing or is getting rewards
  if (
    !isCartTotalNegative &&
    (isAuthenticated || !enableSignupAfterCart) &&
    isRestaurantPosConnectivityStatusLoading
  ) {
    return <CartLoading />;
  }

  const renderCartErrors = () => {
    if (showUnavailableItemsModal) {
      return (
        <OrderUnavailableModal
          items={unavailableCartEntries}
          onDismiss={handleUnavailableModalDismiss}
        />
      );
    }

    if (openUnavailableErrorModal) {
      return <CartPageError />;
    }

    return null;
  };

  const hasCartErrors = isMobile && (openUnavailableErrorModal || showUnavailableItemsModal);

  return (
    <>
      <KeyboardAwareScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ flexShrink: 0 }}
        testID={CART_PAGE}
        innerRef={ref => {
          cartFullPageScreenRef.current = ref as any;
        }}
      >
        {!isMobile && <LoyaltyRewardsBalance />}
        <CartContentWrapper>
          {hasCartErrors && renderCartErrors()}
          <CartContent
            hasAddOns={addOnSections.length > 0}
            checkoutPaymentMethod={checkoutPaymentMethod}
            cartFullPageScreenRef={cartFullPageScreenRef?.current}
          />
          {loyaltySurpriseOffersEnabled && (
            <>
              <ModalSurprise />
              <ModalUpsize />
            </>
          )}
        </CartContentWrapper>
      </KeyboardAwareScrollView>

      {showUnavailableItemsModal && (
        <OrderUnavailableModal
          items={unavailableCartEntries}
          onDismiss={handleUnavailableModalDismiss}
        />
      )}

      {cardSuccessfullyAdded && (
        <VisuallyHidden
          accessibilityLabel={formatMessage({ id: 'yourNewPaymentMethodHasBeenSuccessfullySaved' })}
        />
      )}
    </>
  );
};
